import { useTranslation } from 'react-i18next'
import type { Match, Prediction } from '@/types/bracket'
import { ROUND_POINTS, EXACT_SCORE_BONUS } from '@/types/scoring'
import { useLocalizedTeamName } from '@/utils/teamNames'
import { formatCountdown } from '@/hooks/useLockStatus'

interface MatchCardProps {
  match: Match
  prediction?: Prediction
  localWinnerId?: string
  localHomeScore?: number | null
  localAwayScore?: number | null
  isLocked: boolean
  isGhosted?: boolean
  onPickTeam: (matchId: string, teamId: string) => void
  onScoreChange: (matchId: string, home: number | null, away: number | null) => void
  showScoreInputs?: boolean
  nowSecs?: number
  lockBufferSecs?: number
}

export function MatchCard({
  match,
  prediction,
  localWinnerId,
  localHomeScore,
  localAwayScore,
  isLocked,
  isGhosted = false,
  onPickTeam,
  onScoreChange,
  showScoreInputs = false,
  nowSecs,
  lockBufferSecs = 1800,
}: MatchCardProps) {
  const { t } = useTranslation()
  const getTeamName = useLocalizedTeamName()
  const effectiveWinnerId = localWinnerId ?? prediction?.predicted_winner_id
  const pts = ROUND_POINTS[match.round]

  // Per-match lock countdown
  const now = nowSecs ?? Math.floor(Date.now() / 1000)
  const lockAt = match.scheduled_at ? match.scheduled_at - lockBufferSecs : null
  const secsUntilLock = lockAt ? Math.max(0, lockAt - now) : null
  const showLockCountdown = secsUntilLock !== null && secsUntilLock > 0 && secsUntilLock < 7200
  const justLocked = isLocked && match.status === 'SCHEDULED' && secsUntilLock === 0

  const getTeamClass = (teamId: string | null) => {
    if (!teamId) return ''
    if (match.status === 'FINISHED') {
      if (teamId === match.winner_team_id) return 'team-slot-winner'
      return 'team-slot-loser'
    }
    if (effectiveWinnerId === teamId) return 'team-slot-selected'
    if (!isLocked && (match.home_team_id || match.away_team_id)) return 'team-slot-selectable'
    return 'team-slot'
  }

  const renderTeam = (teamId: string | null, team: Match['home_team']) => {
    const isPickable = !isLocked && teamId && match.status !== 'FINISHED'
    return (
      <div
        className={getTeamClass(teamId)}
        onClick={() => isPickable && teamId && onPickTeam(match.id, teamId)}
      >
        <span className="text-lg">{team?.flag_emoji ?? '🏳'}</span>
        <span className="text-sm font-medium truncate flex-1 min-w-0">
          {team ? getTeamName(team.id, team.name) : 'TBD'}
        </span>
        {match.status === 'FINISHED' && (
          <span className="text-sm font-bold tabular-nums ml-auto">
            {teamId === match.home_team_id ? match.home_score : match.away_score}
          </span>
        )}
        {effectiveWinnerId === teamId && match.status !== 'FINISHED' && (
          <span className="text-emerald-400 text-xs ml-auto">✓</span>
        )}
      </div>
    )
  }

  const isPreliminary =
    match.round === 'R32' &&
    match.teams_confirmed === 0 &&
    (match.home_team_id !== null || match.away_team_id !== null)

  if (isGhosted) {
    return (
      <div className="card p-2 min-w-[180px] max-w-[220px] opacity-25 pointer-events-none select-none">
        <div className="flex items-center justify-between mb-1.5 px-1">
          <span className="text-[10px] text-white/40 uppercase tracking-wide">{match.round} #{match.match_number}</span>
          <span className="text-[10px] text-gold-400/50 font-semibold">{pts}pts</span>
        </div>
        <div className="flex flex-col gap-1">
          {(['TBD', 'TBD'] as const).map((_, i) => (
            <div key={i} className="team-slot"><span className="text-lg">🏳</span><span className="text-sm text-white/30">TBD</span></div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={`card p-2 min-w-[180px] max-w-[220px] relative ${isPreliminary ? 'border border-amber-500/40' : ''}`}>
      {/* Round pts badge */}
      <div className="flex items-center justify-between mb-1.5 px-1">
        <span className="text-[10px] text-white/40 uppercase tracking-wide">{match.round} #{match.match_number}</span>
        <div className="flex items-center gap-1.5">
          {showLockCountdown && (
            <span className="text-[9px] font-mono text-amber-400 bg-amber-400/10 px-1 rounded">
              🔐 {formatCountdown(secsUntilLock!)}
            </span>
          )}
          {justLocked && (
            <span className="text-[9px] text-white/30">🔒</span>
          )}
          <span className="text-[10px] text-gold-400 font-semibold">{pts}pt{pts !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Preliminary badge */}
      {isPreliminary && (
        <div className="flex justify-center mb-1.5">
          <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full">
            {t('bracket.preliminary')}
          </span>
        </div>
      )}

      <div className="flex flex-col gap-1">
        {renderTeam(match.home_team_id, match.home_team)}
        <div className="text-center text-xs text-white/30 my-0.5">vs</div>
        {renderTeam(match.away_team_id, match.away_team)}
      </div>

      {/* Exact score inputs */}
      {showScoreInputs && !isLocked && effectiveWinnerId && (
        <div className="mt-2 pt-2 border-t border-white/10">
          <p className="text-[10px] text-white/40 mb-1.5 text-center">Score (+{EXACT_SCORE_BONUS}pts bonus)</p>
          <div className="flex items-center gap-1 justify-center">
            <input
              type="number" min={0} max={20}
              value={localHomeScore ?? prediction?.predicted_home_score ?? ''}
              onChange={e => {
                const v = e.target.value === '' ? null : parseInt(e.target.value)
                onScoreChange(match.id, v, localAwayScore ?? prediction?.predicted_away_score ?? null)
              }}
              className="w-10 bg-pitch-800 border border-white/20 rounded text-center text-white text-sm py-1 focus:outline-none focus:border-emerald-500"
              placeholder="0"
            />
            <span className="text-white/40 text-sm font-bold">-</span>
            <input
              type="number" min={0} max={20}
              value={localAwayScore ?? prediction?.predicted_away_score ?? ''}
              onChange={e => {
                const v = e.target.value === '' ? null : parseInt(e.target.value)
                onScoreChange(match.id, localHomeScore ?? prediction?.predicted_home_score ?? null, v)
              }}
              className="w-10 bg-pitch-800 border border-white/20 rounded text-center text-white text-sm py-1 focus:outline-none focus:border-emerald-500"
              placeholder="0"
            />
          </div>
        </div>
      )}

      {match.status === 'LIVE' && (
        <div className="mt-1.5 text-center">
          <span className="badge-live text-[10px]">● LIVE</span>
        </div>
      )}
    </div>
  )
}
